import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, X, Loader2, Calendar, Clock, MapPin, IndianRupee } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const isCredits = booking?.kind === 'credits';

  useEffect(() => {
    const verify = async () => {
      const plinkId = searchParams.get('razorpay_payment_link_id');
      const paymentId = searchParams.get('razorpay_payment_id');
      const refId = searchParams.get('razorpay_payment_link_reference_id');
      const plinkStatus = searchParams.get('razorpay_payment_link_status');
      const signature = searchParams.get('razorpay_signature');

      if (!plinkId || !paymentId) {
        setStatus('success');
        return;
      }

      try {
        const res = await axios.post(`${API}/api/bookings/verify-payment-link`, {
          razorpay_payment_link_id: plinkId,
          razorpay_payment_id: paymentId,
          razorpay_payment_link_reference_id: refId,
          razorpay_payment_link_status: plinkStatus,
          razorpay_signature: signature,
        });
        setStatus('confirmed');
        setBooking(res.data);
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail === 'Booking already confirmed' || err.response?.data?.status === 'confirmed') {
          setStatus('confirmed');
          setBooking(err.response?.data);
        } else {
          setStatus('success');
        }
      }
    };
    verify();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#f0ebe0] flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-lg" data-testid="payment-result-card">
        <CardContent className="p-8 text-center space-y-4">
          {status === 'verifying' ? (
            <>
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
              <h2 className="text-xl font-bold">Verifying Payment...</h2>
              <p className="text-muted-foreground text-sm">Please wait while we confirm your payment.</p>
            </>
          ) : status === 'confirmed' || status === 'success' ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Payment Successful!</h2>
              <p className="text-muted-foreground">
                {isCredits
                  ? (booking?.message || 'Credits added to your account') + '. Log in to your Clayworks account to use them.'
                  : 'Your booking has been confirmed. You will receive a confirmation email shortly.'}
              </p>
              {booking?.booking_id && (
                <p className="text-xs text-muted-foreground mt-2">
                  Booking ID: {booking.booking_id}
                </p>
              )}
              <div className="pt-4 border-t border-border text-left space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Payment received</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>{isCredits ? 'Credits added to your account' : 'Booking confirmed'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Confirmation email sent</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Payment Failed</h2>
              <p className="text-muted-foreground">
                {error || 'Something went wrong with payment verification. Please contact support.'}
              </p>
            </>
          )}
          <p className="text-xs text-muted-foreground pt-4">
            For queries, contact Sales@clayworks.in or call 9035331864
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
